/**
 * Clear User IDs Action
 *
 * Synchronous action that clears userId and consumerId from context.
 * This is an ACTION (not an actor) - fire-and-forget, no async.
 */

import { assign } from "xstate";

/**
 * Action metadata
 */
export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

/**
 * Action with metadata wrapper
 */
export interface ActionWithMetadata {
  impl: ReturnType<typeof assign>;
  metadata: ActionMetadata;
}

/**
 * Clear User IDs Action
 *
 * Clears userId and consumerId from machine context.
 * Used when user skips OTP to ensure no partial authentication data remains.
 *
 * Example:
 * ```json
 * {
 *   "event": "SKIP",
 *   "actions": [{ "type": "clearUserIds" }],
 *   "target": "nextState"
 * }
 * ```
 */
export const clearUserIds: ActionWithMetadata = {
  impl: assign(({ context }) => {
    return {
      userId: undefined,
      consumerId: undefined,
      userIdBackground: context.userId,
      consumerIdBackground: context.consumerId,
    };
  }),
  metadata: {
    displayName: "clearUserIds",
    description: "Clear userId and consumerId from context",
    category: "context",
  },
};
